import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import { TopBarMenuItem } from 'topBar/components/TopBar';
import { EventDetail } from 'events/_types';
import { useToggleEventPin } from 'events/hooks/useEvent';
import useEventShareMenuItems from 'events/hooks/useEventShareMenuItems';
import { usePosthogCapture } from 'events/posthogCapture/_index';

interface UseEventMenuItemsOptions {
  onShowHistory?: () => void;
}

export default function useEventMenuItems(
  event: EventDetail | undefined,
  options?: UseEventMenuItemsOptions
): TopBarMenuItem[] {
  const { t } = useTranslation('pageEvents');
  const eventsCapture = usePosthogCapture();
  const { mutate: toggleEventPin } = useToggleEventPin(event?.id ?? '');
  const shareMenuItems = useEventShareMenuItems(event);
  const onShowHistory = options?.onShowHistory;

  return useMemo<TopBarMenuItem[]>(() => {
    if (!event) return [];
    const pinItem: TopBarMenuItem = {
      label: event.pinned ? t('eventPage.unpinEvent') : t('eventPage.pinEvent'),
      icon: <General.Pin01 size={20} />,
      onClick: () => {
        const nextPinned = !event.pinned;
        toggleEventPin(nextPinned);
        if (nextPinned) {
          eventsCapture.eventPinned({ event_id: event.id });
        } else {
          eventsCapture.eventUnpinned({ event_id: event.id });
        }
      },
    };
    const items: TopBarMenuItem[] = [pinItem, ...shareMenuItems];
    if (onShowHistory) {
      items.push({
        label: t('eventPage.versionHistory'),
        icon: <General.ClockRewind size={20} />,
        onClick: onShowHistory,
      });
    }
    return items;
  }, [event, shareMenuItems, t, toggleEventPin, eventsCapture, onShowHistory]);
}
